import { z } from "zod";

/**
 * Sprint 4 #2 — Microbiology MVP.
 *
 * Two tightly-coupled resources: organisms (per lab_order, polymicrobial
 * cultures have many rows) and AST sensitivities (per organism, one row
 * per antibiotic tested).
 */

export const organismTypeSchema = z.enum([
	"bacteria",
	"fungus",
	"virus",
	"parasite",
	"other",
]);
export type OrganismType = z.infer<typeof organismTypeSchema>;

export const astResultSchema = z.enum(["S", "I", "R"]);
export type AstResult = z.infer<typeof astResultSchema>;

// ===== Organism =====

export const cultureOrganismSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	lab_order_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	organism_name: z.string(),
	organism_type: organismTypeSchema.nullable(),
	growth_quantity: z.string().nullable(),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	identified_at: z.string().datetime(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type CultureOrganismApi = z.infer<typeof cultureOrganismSchema>;

export const createCultureOrganismInputSchema = z.object({
	lab_order_id: z.string().uuid(),
	organism_name: z.string().min(1).max(200),
	organism_type: organismTypeSchema.nullable().optional(),
	growth_quantity: z.string().max(120).nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type CreateCultureOrganismInput = z.infer<
	typeof createCultureOrganismInputSchema
>;

export const updateCultureOrganismInputSchema = z.object({
	organism_name: z.string().min(1).max(200).optional(),
	organism_type: organismTypeSchema.nullable().optional(),
	growth_quantity: z.string().max(120).nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type UpdateCultureOrganismInput = z.infer<
	typeof updateCultureOrganismInputSchema
>;

// ===== Sensitivity (AST) =====

export const organismSensitivitySchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	organism_id: z.string().uuid(),
	antibiotic_name: z.string(),
	result: astResultSchema,
	mic_value: z.string().nullable(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type OrganismSensitivityApi = z.infer<typeof organismSensitivitySchema>;

export const astItemSchema = z.object({
	antibiotic_name: z.string().min(1).max(120),
	result: astResultSchema,
	mic_value: z.string().max(60).nullable().optional(),
	notes: z.string().max(500).nullable().optional(),
});
export type AstItemInput = z.infer<typeof astItemSchema>;

/**
 * Batch upsert AST for a single organism. The service deletes any
 * existing sensitivities for the organism and re-inserts the new set
 * within a transaction, so the caller can replace the entire AST panel
 * in one round trip without worrying about partial state.
 */
export const recordAstInputSchema = z.object({
	organism_id: z.string().uuid(),
	items: z.array(astItemSchema).min(1).max(60),
});
export type RecordAstInput = z.infer<typeof recordAstInputSchema>;

/** Combined view returned by the per-order endpoint. */
export const cultureBundleSchema = z.object({
	organisms: z.array(
		cultureOrganismSchema.extend({
			sensitivities: z.array(organismSensitivitySchema),
		}),
	),
});
export type CultureBundleApi = z.infer<typeof cultureBundleSchema>;
